import { computed } from '@ember/object';
import BaseActualFilterAmplitude from '../../implementation-common/base-actual-filter-amplitude';
import * as Complex from '../../../process-data/complex';

export default BaseActualFilterAmplitude.extend({
  r1: null,
  r2: null,
  r5: null,
  c1: null,
  c2: null,
  opAmp: null,
  complexAlphaPlus: Complex.createComplex(0, 0),
  complexBetaPlus: Complex.createComplex(0, 0),
  complexDenominator: computed('r1', 'r2', 'r5', 'c1', 'c2', 'sImaginary', 'sSquared', function () {
    var sImaginary = this.sImaginary;
    var sSquared = this.sSquared;

    var c1 = this.c1;
    var c2 = this.c2;
    var r1 = this.r1;
    var r2 = this.r2;
    var r5 = this.r5;

    var denominatorRealTerm = sSquared * c1 * c2 * r1 * r2 * r5 + r1 + r5;
    var denominatorImaginaryTerm = sImaginary * (c2 * r1 * r2 + c2 * r1 * r5 + c2 * r2 * r5 + c1 * r1 * r5);
    var result = Complex.createComplex(denominatorRealTerm, denominatorImaginaryTerm);
    return result;
  }),
  complexZlookbackDenominator: computed('r1', 'r2', 'r5', 'c1', 'c2', 'sImaginary', 'sSquared', function () {
    var sImaginary = this.sImaginary;
    var sSquared = this.sSquared;

    var c1 = this.c1;
    var c2 = this.c2;
    var r1 = this.r1;
    var r2 = this.r2;
    var r5 = this.r5;

    var ZlookbackDenomnatorRealTerm = sSquared * (c1 * c2 * r1 * r2 + c1 * c2 * r1 * r5) + 1;
    var ZlookbackDenomnatorImaginaryTerm = sImaginary * (c1 * r1 + c2 * r2 + c2 * r5);
    var result = Complex.createComplex(ZlookbackDenomnatorRealTerm, ZlookbackDenomnatorImaginaryTerm);
    return result;
  }),
  complexFFDenominator: computed('complexROut', 'complexZlookbackDenominator', 'complexDenominator', function () {
    var complexROut = this.complexROut;
    var complexZlookbackDenominator = this.complexZlookbackDenominator;
    var complexDenominator = this.complexDenominator;

    var result = complexROut.mult(complexZlookbackDenominator).plus(complexDenominator);
    return result;
  }),
  complexRParallel: computed('complexROut', 'complexFFDenominator', 'complexDenominator', function () {
    var complexROut = this.complexROut;
    var complexFFDenominator = this.complexFFDenominator;
    var complexDenominator = this.complexDenominator;

    var rParallelNumerator = complexDenominator.mult(complexROut);
    var rParallelDenominator = complexFFDenominator;
    var result = rParallelNumerator.divide(rParallelDenominator);
    return result;
  }),
  complexFF: computed('c2', 'complexFFDenominator', 'opAmp.rOut', 'r2', 'r5', 'sImaginary', function () {
    var complexFFDenominator = this.complexFFDenominator;
    var sImaginary = this.sImaginary;

    var rOut = this.opAmp.rOut;
    var c2 = this.c2;
    var r2 = this.r2;
    var r5 = this.r5;

    var FFnumerator = Complex.createComplex(rOut, sImaginary * (c2 * r2 * rOut + c2 * r5 * rOut));
    var result = FFnumerator.divide(complexFFDenominator);
    return result;
  }),
  complexAlphaMinus: computed('r5', 'complexDenominator', function () {
    var complexDenominator = this.complexDenominator;
    var r5 = this.r5;

    var result = Complex.createComplex(r5, 0).divide(complexDenominator);
    return result;
  }),
  complexBetaMinus: computed('r1', 'r5', 'c1', 'sImaginary', 'complexDenominator', function () {
    var sImaginary = this.sImaginary;
    var complexDenominator = this.complexDenominator;
    var c1 = this.c1;
    var r1 = this.r1;
    var r5 = this.r5;

    var complexBetaminusNumerator = Complex.createComplex(-1 * r5, -1 * c1 * r1 * r5 * sImaginary).plus(
      complexDenominator,
    );
    var result = complexBetaminusNumerator.divide(complexDenominator);
    return result;
  }),
  inputImpedance: computed(
    'r1',
    'r2',
    'r5',
    'c1',
    'c2',
    'sImaginary',
    'complexDenominator',
    'complexAmplitude',
    function () {
      var r1 = this.r1;
      var r2 = this.r2;
      var r5 = this.r5;
      var c1 = this.c1;
      var c2 = this.c2;

      var sImaginary = this.sImaginary;
      var complexOne = Complex.createComplex(1, 0);
      var complexAmplitude = this.complexAmplitude;

      var y1 = Complex.createComplex(r2, -1 / (c2 * sImaginary));
      y1 = complexOne.divide(y1);
      y1.real = y1.real + 1 / r5;

      var bottom = y1.mult(complexAmplitude);
      bottom = y1.subs(bottom);
      bottom.imaginary += c1 * sImaginary;

      var top = Complex.createComplex(1, c1 * r1 * sImaginary);
      var y1r1 = y1;
      y1r1.real *= r1;
      y1r1.imaginary *= r1;
      top = top.plus(y1r1);

      var result = top.divide(bottom);
      return result;
    },
  ),
  computeMagnitude(complexAlphaMinus, complexFF) {
    let complexAlphaPlus = this.complexAlphaPlus;
    let complexA = this.complexA;
    let amplitudeDenominator = this.amplitudeDenominator;
    let amplitudeNumerator = this._computeAmplitudeNumerator(complexAlphaPlus, complexAlphaMinus, complexA, complexFF);
    let complexAmplitude = this._computeComplexAmplitude(amplitudeNumerator, amplitudeDenominator);
    return complexAmplitude.magnitude();
  },
});
